"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, ShieldCheck, Clock, FileCheck2, ArrowRight, PhoneCall, CircleCheck as CheckCircle2, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
const keyHighlights = [{
  icon: Clock,
  title: "Rapid Emergency Response",
  description: "Dedicated on-site field engineers across Ireland & the UK."
}, {
  icon: ShieldCheck,
  title: "GMP & GLP Compliant",
  description: "Calibrations and repairs adhering to strict regulatory protocols."
}, {
  icon: FileCheck2,
  title: "Traceable Documentation",
  description: "Detailed service history, audit logs, and qualification certificates."
}];
const capabilities = ["OEM-Trained Technical Engineers", "Scheduled Preventative Maintenance", "Emergency Breakdown Diagnosis & Repair", "Multi-Vendor Analytical Hardware Coverage"];
export default function MaintenanceHero() {
  const shouldReduceMotion = useReducedMotion();
  const heroMotion = {
    initial: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 16
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  };
  return <section id="maintenancerepairhero" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden border-b border-border">
      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Content */}
          <motion.div initial={heroMotion.initial} animate={heroMotion.animate} transition={heroMotion.transition} className="lg:col-span-7 space-y-6">
            {/* Breadcrumbs */}
            <nav aria-label="breadcrumb">
              <ol className="flex flex-wrap items-center gap-1.5 text-xs sm:text-sm text-muted-foreground">
                <li className="inline-flex items-center gap-1.5">
                  <Link to="/HomePage" className="transition-colors hover:text-foreground">
                    Home
                  </Link>
                </li>
                <li aria-hidden="true" className="text-muted-foreground/60">
                  <ChevronRight className="size-3.5" />
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <Link to="/ServicesPage" className="transition-colors hover:text-foreground">
                    Services
                  </Link>
                </li>
                <li aria-hidden="true" className="text-muted-foreground/60">
                  <ChevronRight className="size-3.5" />
                </li>
                <li className="inline-flex items-center gap-1.5 font-medium text-foreground" aria-current="page">
                  Preventive Maintenance &amp; Repairs
                </li>
              </ol>
            </nav>

            <div className="inline-flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 font-medium text-xs tracking-wide">
                <Wrench className="size-3.5 text-primary" />
                Lifecycle Support & Instrument Care
              </Badge>
            </div>

            <div className="space-y-4">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground leading-tight">
                Analytical Equipment Maintenance & Fast-Response Repair
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl font-normal leading-relaxed">
                Protect instrument uptime and data integrity in regulated laboratories and manufacturing facilities. Our certified engineers deliver preventative maintenance, precision calibration, and emergency diagnostic repairs across Ireland and the UK.
              </p>
            </div>

            {/* Feature Bullets */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-2">
              {capabilities.map((item, index) => <div key={item} data-index={index} className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span className="text-sm font-medium text-foreground">{item}</span>
                </div>)}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-3.5 pt-4">
              <Button size="lg" asChild className="h-11 px-6">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <span>Book Maintenance Visit</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button size="lg" variant="outline" asChild className="h-11 px-6">
                <Link to="/ServicesPage" className="inline-flex items-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>View Service Contracts</span>
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Hero Visual & Service Badge */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          scale: 1
        } : {
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-lg">
              <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/maintenance_repair_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/maintenance_repair_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/maintenance_repair_hero.png-wvc-sizes" alt="Laboratory engineer performing maintenance and calibration on analytical testing instrument" loading="lazy" decoding="async" className="size-full object-cover" />
              </div>

              {/* Inset floating highlight bar */}
              <div className="p-5 bg-card/95 border-t border-border space-y-3">
                <div className="flex items-center justify-between text-xs text-muted-foreground border-b border-border/60 pb-2">
                  <span>Service Availability</span>
                  <span className="font-semibold text-foreground">Ireland & UK Nationwide</span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Standard Response SLA</span>
                  <span className="font-semibold text-primary">Within 24-48 Hours</span>
                </div>
              </div>
            </div>
          </motion.div>

        </div>

        {/* Highlights Row */}
        <div className="mt-14 pt-10 border-t border-border grid grid-cols-1 md:grid-cols-3 gap-6">
          {keyHighlights.map((item, index) => {
          const IconComponent = item.icon;
          return <Card key={item.title} data-index={index} className="border-border bg-card shadow-xs transition-[box-shadow,border-color] duration-300 hover:border-primary/40 hover:shadow-md">
                <CardContent className="p-5 flex items-start gap-4">
                  <div className="rounded-lg bg-primary/10 p-2.5 text-primary shrink-0">
                    <IconComponent className="size-5" />
                  </div>
                  <div className="space-y-1">
                    <h2 className="text-base font-semibold text-foreground font-default">
                      {item.title}
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>

      </div>
    </section>;
}